import {TransactionStatus} from '@sollapay/enums';
import {Icon, Typography} from '@sollapay/ui';
import {PaymentMethodBadge} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import {PAYMENT_METHOD_BADGE_I18N} from '@/features/buyers/config/buyers.config';
import {getAssetFeedSubtitle} from '@/features/buyers/utils';
import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BaseTransactionRow} from './BaseTransactionRow';

import type {TrustAccountPayinTransactionDTO} from '@sollapay/types';
import type {FC} from 'react';

interface PayinTransactionRowProps {
  transaction: TrustAccountPayinTransactionDTO;
}

export const PayinTransactionRow: FC<PayinTransactionRowProps> = ({transaction}) => {
  const {t} = useTranslation('trustAccounts');
  const {allowsMultipleAssets} = useTrustTypeStrategy();
  const isInflight =
    transaction.status === TransactionStatus.INFLIGHT ||
    transaction.status === TransactionStatus.VOIDED;

  const directionIcon = (
    <Icon data-slot="direction-icon" name="sollapay:payin" className="size-8 shrink-0" />
  );

  const assetSubtitle = getAssetFeedSubtitle(transaction.asset, t, allowsMultipleAssets);

  const nameSection = (
    <>
      {transaction.buyerName != null && (
        <Typography size="sm" weight="medium">
          {transaction.buyerName}
        </Typography>
      )}
      {assetSubtitle != null && (
        <Typography size="xs" color="tertiary">
          {assetSubtitle}
        </Typography>
      )}
    </>
  );

  const badgeSection =
    transaction.initiationMethod != null ? (
      <PaymentMethodBadge method={transaction.initiationMethod} {...PAYMENT_METHOD_BADGE_I18N} />
    ) : null;

  return (
    <BaseTransactionRow
      directionIcon={directionIcon}
      nameSection={nameSection}
      badgeSection={badgeSection}
      status={transaction.status}
      effectiveDate={transaction.effectiveDate}
      amount={transaction.amount}
      isCredit={true}
      isInflight={isInflight}
      percent={transaction.percent}
    />
  );
};
